import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import type { CloudConfig } from '../../../../common/constants/filters/cloud-config/types';
import type { SearchStoreExperience } from '../../../../common/constants/search';
import {
	useAggAbsoluteUrl,
	useAllFilterExcludedProducts,
	useAnalyticsAttributes,
	useApplicationMode,
	useAutoSelectPrimaryProduct,
	useAvailableProducts,
	useCloudId,
	useExperimentConfig,
	useInitialSpaceARIs,
	usePrimaryProduct,
	useProductDataRegions,
	useProductEdition,
	useQueryParams,
	useSetQueryParams,
	useThirdPartyConfigs,
	useUser,
} from '../../bootstrap';
import { allFilters } from '../filters';
import { SearchStoreContainer } from '../index';

export const SearchStoreProvider = ({
	children,
	experience = 'search-page',
}: {
	children: React.ReactNode;
	experience?: SearchStoreExperience;
}): React.JSX.Element => {
	const [shouldAutoSelectPrimaryProduct, setShouldAutoSelectPrimaryProduct] = useState(true);
	const intl = useIntl();

	const [queryParams] = useQueryParams();
	const [cloudId] = useCloudId();
	const [aggAbsoluteUrl] = useAggAbsoluteUrl();
	const [initialSpaceARIs] = useInitialSpaceARIs();
	const [user] = useUser();
	const products = useAvailableProducts();
	const [allFilterExcludedProducts] = useAllFilterExcludedProducts();
	const [primaryProduct] = usePrimaryProduct();
	const [experimentConfig] = useExperimentConfig();
	const [productEdition] = useProductEdition();
	const [productDataRegions] = useProductDataRegions();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const setQueryParams = useSetQueryParams(queryParams);

	const applicationMode = useApplicationMode();

	const primaryProductToAutoSelect = useAutoSelectPrimaryProduct();

	const config = useMemo(
		() =>
			({
				aggAbsoluteUrl,
				ari: '',
				cloudId,
				experimentConfig,
				initialSpaceARIs,
				intl,
				productDataRegions,
				productEdition,
				userId: user?.id || '',
				product: queryParams?.product,
				primaryProduct,
				thirdPartyConfigs,
				activationId: analyticsAttributes?.activationId,
			}) satisfies CloudConfig,
		[
			aggAbsoluteUrl,
			cloudId,
			experimentConfig,
			initialSpaceARIs,
			intl,
			productDataRegions,
			productEdition,
			user?.id,
			queryParams?.product,
			primaryProduct,
			thirdPartyConfigs,
			analyticsAttributes?.activationId,
		],
	);

	const queryParamsInUse = useMemo(() => {
		const {
			lastModified_lt: _lastModified_lt,
			lastModified_gt: _lastModified_gt,
			...queryParamsInUse
		} = queryParams;
		return queryParamsInUse;
	}, [queryParams]);

	// primaryProductToAutoSelect will be set to the primary product if the user is in unified search
	// and no product is selected
	// This is to ensure that the primary product is selected by default for unified search
	useEffect(() => {
		if (products && products.length > 0) {
			if (shouldAutoSelectPrimaryProduct && primaryProductToAutoSelect) {
				setQueryParams({ ...queryParams, product: primaryProductToAutoSelect });
			}
			setShouldAutoSelectPrimaryProduct(false);
		}
	}, [
		applicationMode,
		primaryProduct,
		primaryProductToAutoSelect,
		products,
		queryParams,
		setQueryParams,
		shouldAutoSelectPrimaryProduct,
	]);

	return (
		<SearchStoreContainer
			queryParams={queryParamsInUse}
			allFilters={allFilters}
			config={config}
			setQueryParams={setQueryParams}
			availableProducts={products}
			allFilterExcludedProducts={allFilterExcludedProducts}
			applicationMode={applicationMode}
			primaryProduct={primaryProduct}
			thirdPartyConfigs={thirdPartyConfigs}
			experience={experience}
		>
			{children}
		</SearchStoreContainer>
	);
};
